import classNames from 'classnames';
import Button from 'components/ui-new/Button';
import { useAppContext } from 'context/state';
import PurchaseRepository from 'data/repositories/PurchaseRepository';
import Cookies from 'js-cookie';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC } from 'react';
import {
  CookiesType,
  CurrencySymbols,
  ModalType,
  MonetizationTariff,
  ProductType,
} from 'types/enums';
import { getCurrencySymbol } from 'utils/commonObjects';
import { getCountedItemKey } from 'utils/getCountedItemKey';
import Badge from '../../../shared/Badge';
import styles from './style.module.scss';

interface Props {
  tariff: MonetizationTariff;
  totalCount: number;
  isActive: boolean;
  currentCount: number;
  price: number;
  currency: CurrencySymbols;
  expiredIn: string;
  isPartialPayment: boolean;
  id: number;
  isHiddenWarning: boolean;
  isShowAddInfo: boolean;
  type: ProductType;
}

const Tariff: FC<Props> = ({
  currency,
  currentCount,
  expiredIn,
  price,
  tariff,
  totalCount,
  isHiddenWarning,
  isShowAddInfo,
  isActive,
  isPartialPayment,
  id,
  type,
}) => {
  const t = useTranslations();
  const appContext = useAppContext();
  const countingKey = getCountedItemKey(type);

  const handleClickPartialPaymentText = () => {
    appContext.showModal(ModalType.IncompletePayment);
  };

  const handleClickPartialPaymentPayExtra = async () => {
    const res = await PurchaseRepository.getPaymentLinksFromId(
      Cookies.get(CookiesType.accessToken),
      id,
    );

    window.location.href = res.paymentParams.link;
  };

  return (
    <div
      className={classNames(styles.tariff, {
        [styles.tariff__disabled]: isShowAddInfo,
        [styles.tariff__warning]: !isHiddenWarning,
        [styles.tariff__partialPayment]: isPartialPayment,
      })}>
      <div className={styles.info}>
        <div className={styles.nameBlock}>
          <span className={styles.name}>{t(tariff)}</span>
        </div>
        <Badge isActive={isActive} tariff={tariff} isPartialPayments={isPartialPayment} />
      </div>
      <div
        className={classNames(styles.placementsCount, {
          [styles.placementsCount__warning]: !isHiddenWarning,
        })}>
        {totalCount === 1 ? (
          t(countingKey, { count: 1 })
        ) : (
          <>
            {t(countingKey, { count: currentCount })}{' '}
            <span className={styles.placements__of}>{t('shop_from')}</span> {totalCount}
          </>
        )}
      </div>
      {totalCount > 1 ? (
        <progress
          max={totalCount}
          value={currentCount}
          className={classNames(styles.progress, {
            [styles.progress__partialPayment]: isPartialPayment,
          })}></progress>
      ) : null}
      <div
        className={classNames(styles.inlineInfo, {
          [styles.inlineInfo__warning]: !isHiddenWarning,
        })}>
        <span className={styles.inlineInfo__name}>{t('price')}</span>
        <span className={styles.inlineInfo__value}>{`${getCurrencySymbol(currency)}${price}`}</span>
      </div>
      <div className={classNames(styles.inlineInfo, styles.inlineInfo__disableBorder)}>
        <span className={styles.inlineInfo__name}>{t('expired_in')}</span>
        <span className={styles.inlineInfo__value__silver}>{expiredIn}</span>
      </div>
      <div
        className={classNames(styles.partialPayment, {
          [styles.partialPayment__disabled]: !isPartialPayment,
        })}>
        <div className={styles.partialPayment__warning} onClick={handleClickPartialPaymentText}>
          <Image src='/icons/warning.svg' width={12} height={12} alt='warning' />
          <span className={styles.partialPayment__text}>{t('whats_the_problem')}</span>
        </div>
        <Button
          className={styles.partialPayment__btn}
          variant='grey'
          size='S'
          onClick={handleClickPartialPaymentPayExtra}>
          {t('pay_extra')}
        </Button>
      </div>
    </div>
  );
};

export default Tariff;
